/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState } from 'react';
import { observer } from 'mobx-react';
import { Button as CarbonButton, Popover } from '@carbon/react';
import { ChevronDown, ChevronUp, Enterprise, Folders } from '@carbon/icons-react';
import { Link } from 'react-router-dom';

import { VersionCreationModal } from 'features/create-version';
import { isContainedInProcessApplication } from 'utils/helpers';
import { organizationStore, processApplicationStore } from 'stores';
import { Download, Error, Info, Milestone, Saved } from 'icons';
import { Button, IconButton } from 'primitives';
import { Dropdown } from 'components';
import { exportElementTemplate } from 'utils/file-io';
import { hasAccess, actions } from 'utils/user-access';
import { buildPath } from 'utils/build-path';

import { elementTemplateEditorStore } from './element-template-editor-store';
import { ElementTemplateIconUploadPopoverContent } from './ElementTemplateIconUploadPopoverContent';
import * as Styled from './ActionBar.styled';
import { PublishConnector } from './features/publish-for-process-application';

const getStatusIcon = (status) => {
  switch (status) {
    case 'progress':
      return <Styled.Spinner width="22" height="22" />;
    case 'done':
      return <Saved width="24" height="24" />;
    case 'error':
      return <Error width="22" height="22" />;
    default:
      return <Info width="18" height="18" />;
  }
};

const PUBLICATION_MODE = {
  PROJECT: 'project',
  ORGANIZATION: 'organization'
};

export const BaseActionBar = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [connectorTemplateIconUploadPopoverContentKey, setConnectorTemplateIconUploadPopoverContentKey] = useState(1);
  const [iconFileData, setIconFileData] = useState(null);
  const [publicationMode, setPublicationMode] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    elementTemplate,
    loading,
    project,
    status,
    isValid,
    addImage,
    setShowDuplicateDialog,
    isIdpConnectorTemplate
  } = elementTemplateEditorStore;

  const handlePublicationToProject = () => {
    setAnchorEl(null);

    setPublicationMode(PUBLICATION_MODE.PROJECT);
  };

  const handleIconUpload = () => {
    addImage(iconFileData.fileContent);
    setIsPopupOpen(false);
    setIconFileData(null);
  };

  const handlePublicationToOrg = () => {
    setAnchorEl(null);

    setPublicationMode(PUBLICATION_MODE.ORGANIZATION);
  };

  const getPublishConnectorButton = () => {
    const isDisabled = loading || !isValid(elementTemplate.content) || isIdpConnectorTemplate;

    if (!elementTemplate) {
      return null;
    }

    if (isContainedInProcessApplication(elementTemplate)) {
      return <PublishConnector connectorTemplate={elementTemplate} isDisabled={isDisabled} />;
    }

    return organizationStore.hasElevatedOrganizationPermissions ? (
      <>
        {elementTemplate.imported && (
          // @ts-expect-error TS2322
          <Button
            className="publish-to-organization-button"
            data-test="publish-to-org"
            onClick={handlePublicationToOrg}
            disabled={isDisabled}
          >
            <Enterprise />
            Publish to organization
          </Button>
        )}

        {!elementTemplate.imported && (
          <>
            {/* @ts-expect-error TS2322 */}
            <Button
              dropdown
              className="publish-button"
              aria-haspopup="true"
              onClick={(evt) => setAnchorEl(evt?.currentTarget)}
              disabled={isIdpConnectorTemplate}
              data-test="publish-connector-template-dropdown"
            >
              Publish
            </Button>

            <Dropdown anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              {/* @ts-expect-error TS2739 */}
              <Dropdown.ListItem
                onClick={handlePublicationToProject}
                data-test="publish-to-project"
                disabled={isDisabled}
              >
                <Styled.PublishOption>
                  <Folders />
                  Publish to project
                </Styled.PublishOption>
              </Dropdown.ListItem>

              {/* @ts-expect-error TS2739 */}
              <Dropdown.ListItem onClick={handlePublicationToOrg} data-test="publish-to-org" $isDisabled={isDisabled}>
                <Styled.PublishOption>
                  <Enterprise />
                  Publish to organization
                </Styled.PublishOption>
              </Dropdown.ListItem>
            </Dropdown>
          </>
        )}
      </>
    ) : !elementTemplate.imported ? (
      // @ts-expect-error TS2322
      <Button
        className="publish-button"
        data-test="publish-connector-template"
        onClick={() => setPublicationMode(PUBLICATION_MODE.PROJECT)}
        disabled={isDisabled}
      >
        Publish
      </Button>
    ) : null;
  };

  return (
    <Styled.ActionBar>
      <Styled.Left>
        {Boolean(status) && (
          // @ts-expect-error TS2769
          <Styled.Status data-test="autosave" $isDanger={status.status === 'error'}>
            {getStatusIcon(status.status)}
            {status.message}
          </Styled.Status>
        )}

        {!hasAccess(project, actions.MODIFY_CONNECTOR_TEMPLATE) && !elementTemplate?.imported && !loading && (
          <Styled.Status data-test="collaborator-info">
            {getStatusIcon()} Only collaborators with write access may modify connector templates.
          </Styled.Status>
        )}

        {elementTemplate?.imported && (
          <Styled.Status data-test="imported-info">
            {getStatusIcon()} This connector template has been downloaded from the Marketplace and cannot be modified.{' '}
            {hasAccess(project, actions.MODIFY_CONNECTOR_TEMPLATE) && 'Duplicate the template to customize it.'}
          </Styled.Status>
        )}
      </Styled.Left>

      {hasAccess(project, actions.MODIFY_CONNECTOR_TEMPLATE) && !elementTemplate.imported && !loading && (
        <Popover open={isPopupOpen} align="bottom-end" caret={false}>
          <Styled.IconUploadButton
            size="sm"
            kind="tertiary"
            renderIcon={isPopupOpen ? ChevronUp : ChevronDown}
            onClick={() => {
              if (isPopupOpen) {
                setIconFileData(null);
              }
              setIsPopupOpen(!isPopupOpen);
              // force re-rendering to clear every input fields
              setConnectorTemplateIconUploadPopoverContentKey(connectorTemplateIconUploadPopoverContentKey + 1);
            }}
            disabled={!isValid(elementTemplate.content) || isIdpConnectorTemplate}
            data-test="upload-icon"
          >
            Add icon
          </Styled.IconUploadButton>
          <ElementTemplateIconUploadPopoverContent
            key={connectorTemplateIconUploadPopoverContentKey}
            iconFileData={iconFileData}
            setIconFileData={setIconFileData}
            handleIconUpload={handleIconUpload}
          />
        </Popover>
      )}

      {hasAccess(project, actions.MODIFY_CONNECTOR_TEMPLATE) && elementTemplate?.imported && (
        <>
          <CarbonButton
            onClick={() => {
              setShowDuplicateDialog(true);
            }}
            kind={
              hasAccess(project, actions.VIEW_VERSIONS) && organizationStore.hasElevatedOrganizationPermissions
                ? 'ghost'
                : 'primary'
            }
          >
            Customize template
          </CarbonButton>
        </>
      )}

      {hasAccess(project, actions.VIEW_VERSIONS) && (
        <>
          {getPublishConnectorButton()}
          <Link
            to={buildPath(
              processApplicationStore.fromAProcessApplication
                ? `/process-applications/${processApplicationStore.processApplication?.id}/versions`
                : `/connector-templates/${elementTemplate.id}/versions`
            )}
          >
            <IconButton title="Show versions" data-test="versions-list" disabled={isIdpConnectorTemplate}>
              <Milestone width="24" height="24" />
            </IconButton>
          </Link>
        </>
      )}

      <IconButton
        disabled={loading}
        onClick={() => exportElementTemplate(elementTemplate)}
        title="Download connector template"
        data-test="export-json"
      >
        <Download width="25" height="24" />
      </IconButton>

      {publicationMode && (
        <VersionCreationModal
          open
          targetIsOrganization={publicationMode === PUBLICATION_MODE.ORGANIZATION}
          onClose={() => setPublicationMode(null)}
          file={elementTemplate}
          origin="publish"
        />
      )}
    </Styled.ActionBar>
  );
};

export const ActionBar = observer(BaseActionBar);
